import { useCallback, useEffect, useRef, useState } from 'react'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { TypingDots } from 'components'
import {
  Clapperboard,
  Image as ImageIcon,
  Images,
  Loader2,
  PenLine,
  Plus,
  Sparkles,
  X,
} from 'lucide-react'
import { useAnalytics } from 'hooks'
import { useIsMobile } from 'hooks/use-mobile'
import { cn } from 'lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from 'components/Popover'
import { PlatformIcon, platformLabel as labelForPlatform } from 'components/PlatformIcon'
import type { PlatformConnectionState } from '../hooks/usePlatformConnections'
import {
  Creative,
  CreativeType,
  type FacebookPostContent,
  type InstagramPostContent,
  type LinkedInPostContent,
  type TikTokVideoContent,
  type XPostContent,
  type YouTubeShortsContent,
  type PinterestPinContent,
} from '../types'
import { type ContentShape, type LivePlatform } from '../store/idea-store'
import { VariantModeProvider } from './variant-mode'

// Short, brand-only labels for the platform strip. Mirrors the convention in
// PostModalPlatformRow — we never want "Instagram Post" / "Facebook Post" here,
// the icon + context already say "post".
import { SideChat } from './SideChat'
import { ByoMediaTray } from './ByoMediaTray'
import { ScorePanel } from './process'
import { ComposerReadOnlyProvider } from './composer-read-only'
import { MobileSectionEditor } from './MobileSectionEditor'
import { type MobileSelectedSection } from './mobile-selection-types'
import { MobileActionBar } from './MobileActionBar'
import { MobileBottomSheet, useExitMount } from './MobileBottomSheet'
import { EditorModal } from './EditorModal'
import { InstagramPostPreview } from '../previews/InstagramPostPreview'
import { FacebookPostPreview } from '../previews/FacebookPostPreview'
import { TikTokVideoPreview } from '../previews/TikTokVideoPreview'
import { LinkedInPostPreview } from '../previews/LinkedInPostPreview'
import { XPostPreview } from '../previews/XPostPreview'
import { YouTubeShortsPreview } from '../previews/YouTubeShortsPreview'
import { PinterestPinPreview } from '../previews/PinterestPinPreview'
import { DARK_PREVIEW_OVERRIDES } from '../previews/preview-utils'
import { CompositionEditorProvider } from '../composition/editor/CompositionEditorProvider'
import { CompositionDock } from '../composition/editor/CompositionDock'
import { ReelTimelineDock } from '../composition/editor/ReelTimeline'
import { SlideFilmstripDock } from '../composition/editor/CarouselCanvasEditor'
import { ComposerEditorBar } from '../composition/editor/ComposerEditorBar'
import { useComposerEditor } from '../composition/editor/CompositionEditorProvider'
import { CanvasDeselectArea } from '../composition/editor/ComposerCanvasMedia'
import { MobileCanvasEditor } from '../composition/editor/MobileCanvasEditor'
import { MobileCarouselEditor } from '../composition/editor/MobileCarouselEditor'
import { MobileVideoEditor } from '../composition/editor/MobileVideoEditor'
import { MobileComposerHome } from '../composition/editor/MobileComposerHome'
import {
  useCompositionStatus,
  usePostHasComposition,
} from '../composition/editor/use-composition-status'
import { carouselSlideFocusKey } from '../utils/carousel-slide-focus'
import { EditableInlineText } from '../previews/EditableInlineText'
import { EditableHashtags } from '../previews/EditableHashtags'
import { useCreativeStore } from '../store/creative-store'
import { toast } from 'sonner'
import { isUserMediaChange, isUserMediaRefusal, saveUserMediaEdit } from '../store/user-media-edit'
import { extractApiError } from '../utils/extract-api-error'
import { useGenerationStatusStore, reconcileCreativeStatus } from '../store/generation-status-store'
import { useChatStatusStore } from '../store/chat-status-store'
import { useSectionContextStore } from '../store/conversation-store'
import { postKeyForCreative } from '../utils/post-key'
import { OpenAPI } from '../../../openapi/core/OpenAPI'
import { request as openApiRequest } from '../../../openapi/core/request'

// ─── Preview Registry ──────────────────────────────────────────────────────

const EMPTY_SET = new Set<string>()

interface PreviewOptions {
  selectedSectionId?: string | null
  onSectionSelect?: (section: MobileSelectedSection | null) => void
  /** When false, the composition image renders read-only (tap drills into the editor) — used by
   *  the mobile preview-first home. Defaults to true (interactive canvas). */
  interactive?: boolean
}

function getPreview(creative: Creative, generatingFields: Set<string>, opts: PreviewOptions = {}) {
  // Mute is shared per post across platform tabs (see PreviewVideo).
  const postKey = postKeyForCreative(creative)
  switch (creative.type) {
    case CreativeType.InstagramPost:
      return (
        <InstagramPostPreview
          content={creative.content as InstagramPostContent}
          generatingFields={generatingFields}
          creativeId={creative.id}
          postKey={postKey}
          selectedSectionId={opts.selectedSectionId}
          onSectionSelect={opts.onSectionSelect}
          interactive={opts.interactive}
        />
      )
    case CreativeType.FacebookPost:
      return (
        <FacebookPostPreview
          content={creative.content as FacebookPostContent}
          generatingFields={generatingFields}
          creativeId={creative.id}
          selectedSectionId={opts.selectedSectionId}
          onSectionSelect={opts.onSectionSelect}
          interactive={opts.interactive}
        />
      )
    case CreativeType.TikTokVideo:
      return (
        <TikTokVideoPreview
          content={creative.content as TikTokVideoContent}
          generatingFields={generatingFields}
          postKey={postKey}
          interactive={opts.interactive}
        />
      )
    case CreativeType.LinkedInPost:
      return (
        <LinkedInPostPreview
          content={creative.content as LinkedInPostContent}
          generatingFields={generatingFields}
          creativeId={creative.id}
          selectedSectionId={opts.selectedSectionId}
          onSectionSelect={opts.onSectionSelect}
          interactive={opts.interactive}
        />
      )
    case CreativeType.XPost:
      return (
        <XPostPreview
          content={creative.content as XPostContent}
          generatingFields={generatingFields}
          creativeId={creative.id}
          selectedSectionId={opts.selectedSectionId}
          onSectionSelect={opts.onSectionSelect}
          interactive={opts.interactive}
        />
      )
    case CreativeType.YouTubeShort:
      return (
        <YouTubeShortsPreview
          content={creative.content as YouTubeShortsContent}
          generatingFields={generatingFields}
          postKey={postKey}
          interactive={opts.interactive}
        />
      )
    case CreativeType.PinterestPin:
      return (
        <PinterestPinPreview
          content={creative.content as PinterestPinContent}
          generatingFields={generatingFields}
          creativeId={creative.id}
          selectedSectionId={opts.selectedSectionId}
          onSectionSelect={opts.onSectionSelect}
          interactive={opts.interactive}
        />
      )
    default:
      return null
  }
}

// ─── Composer Shell ────────────────────────────────────────────────────────

type SheetMode = 'chat' | 'section-editor'

interface PlatformTabsConfig {
  /** Candidates the user is allowed to add (drives the + Add menu). */
  candidates: LivePlatform[]
  /** Currently-in-the-strip platforms, in stable add-order. THIS is the set
   *  the post will publish to — no separate checkbox state. */
  selected: LivePlatform[]
  /** The post's main platform (internal derivation anchor — no longer special in the strip). */
  primary: LivePlatform
  /** Active preview platform, or null when the Post view is active. */
  active: LivePlatform | null
  /** True when the Post tab (the editor) is the active view. */
  postActive: boolean
  /** Post shape — the Post tab is labeled by TYPE (Image / Carousel / Reel). */
  shape?: ContentShape
  /** Platforms whose copy the user customized — their chips show a pen dot. */
  customizedPlatforms: LivePlatform[]
  /** Post tab tapped — route to the Post view. */
  onSelectPost: () => void
  /** Connection state, passed down from ComposerPage's usePlatformConnections
   *  instance — the hook fetches per-instance, so a second call here would
   *  double every integrations GET and OAuthUpdated refetch. */
  connections: Record<LivePlatform, PlatformConnectionState>
  /** Set when the ACTIVE platform variant was hand-edited (pinned): it no
   *  longer auto-updates from the post. Drives the Customized chip. */
  activePinnedAt?: string | null
  /** True when the pinned active variant is behind the post's canonical
   *  (contentRevision > derivedFromRevision). */
  activeBehind?: boolean
  /** "Reset to match post" — unpin + re-derive the active variant. */
  onResetToDerived?: () => void
  /** Tab body click → switch preview (URL sync handled by ComposerPage). */
  onChange: (p: LivePlatform) => void
  /** Picked from the + Add menu — adds AND activates. */
  onAdd: (p: LivePlatform) => void
  /** X badge → remove from the strip. ComposerPage handles confirm-then-
   *  unschedule when the platform's sibling is scheduled. */
  onRemove: (p: LivePlatform) => void
}

interface ComposerShellProps {
  creative: Creative
  /** Post project id — keeps AI chat stable across platform tab switches. */
  projectId?: string
  platformTabs?: PlatformTabsConfig
  /**
   * One Post model: the chat (and the score) always belong to the POST — pass
   * the canonical primary here so the agent revises the post no matter which
   * platform view is on screen. Falls back to `creative` when absent.
   */
  chatCreative?: Creative
  /**
   * One Post model: set on PLATFORM tabs. The tab renders the same canvas
   * surface as the Post tab, and the sections carry the contract via
   * VariantModeProvider (media → "Edit on the post"; text → customize gate
   * until customized, then plain inline editing).
   */
  variantView?: {
    platform: LivePlatform
    platformLabel: string
    customized: boolean
    onCustomizeRequest: () => void
    onGoToPost: () => void
  }
}

/**
 * Composer platform strip. The `selected` set IS the publish set — the user
 * sees a tab for each platform this post will go to. Tab body switches the
 * preview; trailing X removes from the post. A labeled `+ Add platform`
 * button at the end opens a menu of remaining candidates.
 *
 * OAuth is intentionally NOT wired here anymore. It's consolidated at the
 * Schedule step via ConnectToPublishDialog so the user makes one decision
 * ("connect these N platforms now") instead of N decisions scattered
 * across editing.
 */
function PlatformTabStrip({
  candidates,
  selected,
  active,
  postActive,
  shape,
  customizedPlatforms,
  connections,
  activePinnedAt,
  activeBehind,
  onSelectPost,
  onChange,
  onAdd,
  onRemove,
  onResetToDerived,
}: PlatformTabsConfig) {
  const [addOpen, setAddOpen] = useState(false)

  const remaining = candidates.filter(p => !selected.includes(p))

  return (
    <div
      className="flex items-center gap-1 overflow-x-auto border-b border-line bg-bg-2 px-3 py-2"
      role="tablist"
      aria-label="Platforms"
    >
      {/* One Post model: the Post tab IS the editor; every platform chip is a
          preview of it. The structure reads "one post → previewed on N". */}
      <div
        role="tab"
        aria-selected={postActive}
        tabIndex={postActive ? 0 : -1}
        onClick={onSelectPost}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelectPost()
          }
        }}
        className={cn(
          'group relative flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-[10px] px-3 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-acid',
          !postActive && 'hover:bg-bg-3/60'
        )}
      >
        <span
          aria-hidden
          className={cn(
            'absolute right-2 bottom-0 left-2 h-[2px] rounded-t-full bg-acid transition-opacity duration-200',
            postActive ? 'opacity-100' : 'opacity-0'
          )}
        />
        {(() => {
          // The Post tab doubles as the one place the composer says what KIND
          // of post this is (image / carousel / reel).
          const TypeIcon =
            shape === 'carousel'
              ? Images
              : shape === 'short_vertical_video'
                ? Clapperboard
                : ImageIcon
          const typeLabel =
            shape === 'carousel'
              ? 'Carousel'
              : shape === 'short_vertical_video'
                ? 'Reel'
                : shape === 'static_image'
                  ? 'Image'
                  : 'Post'
          return (
            <>
              <TypeIcon size={15} className={postActive ? 'text-paper' : 'text-paper-dim'} />
              <span
                className={cn(
                  'text-[12px] font-medium tracking-tight transition-colors duration-200',
                  postActive ? 'text-paper' : 'text-paper-dim group-hover:text-paper'
                )}
              >
                {typeLabel}
              </span>
            </>
          )
        })()}
      </div>

      <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-line" />
      <span className="shrink-0 pr-1 text-[11px] font-medium text-paper-faint">Previews</span>

      {selected.map(p => {
        const label = labelForPlatform(p)
        const isActive = !postActive && p === active
        const isCustomized = customizedPlatforms.includes(p)

        return (
          <div
            key={p}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange(p)}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onChange(p)
              }
            }}
            className={cn(
              'group relative flex h-9 shrink-0 cursor-pointer items-center gap-2 rounded-[10px] px-2.5 transition-colors duration-150 outline-none focus-visible:ring-2 focus-visible:ring-acid',
              !isActive && 'hover:bg-bg-3/60'
            )}
          >
            {/* Active marker: acid underline (context-strip pattern). */}
            <span
              aria-hidden
              className={cn(
                'absolute right-2 bottom-0 left-2 h-[2px] rounded-t-full bg-acid transition-opacity duration-200',
                isActive ? 'opacity-100' : 'opacity-0'
              )}
            />

            <PlatformIcon channel={p} size={24} />

            <span
              className={cn(
                'truncate text-[12px] font-medium tracking-tight transition-colors duration-200',
                isActive ? 'text-paper' : 'text-paper-dim group-hover:text-paper'
              )}
            >
              {label}
            </span>

            {/* Pen dot — this platform's copy was customized and no longer
                auto-updates from the post. */}
            {isCustomized && (
              <PenLine
                size={11}
                className="shrink-0 text-paper-faint"
                aria-label={`${label} is customized`}
              />
            )}

            {/* Trailing X — every chip is removable: chips are the publish
                set, and under the One Post model no platform is special. */}
            <button
              type="button"
              onClick={e => {
                e.stopPropagation()
                onRemove(p)
              }}
              title={`Remove ${label}`}
              aria-label={`Remove ${label}`}
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-paper-faint transition-colors duration-150 hover:scale-110 hover:bg-bg-4 hover:text-paper active:scale-90"
            >
              <X size={12} />
            </button>
          </div>
        )
      })}

      {/* + Add platform — labeled button at end of strip. Hidden when every
          candidate is already in the strip. */}
      {remaining.length > 0 && (
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Add platform"
              className="group ml-1 flex h-9 shrink-0 cursor-pointer items-center gap-1.5 rounded-[10px] border border-line-bright bg-bg-3 px-2.5 text-[12px] font-medium text-paper transition-all duration-150 outline-none hover:-translate-y-px hover:border-paper-faint hover:bg-bg-4 hover:shadow-[0_4px_10px_-4px_rgba(0,0,0,0.4)] focus-visible:ring-2 focus-visible:ring-acid active:translate-y-0"
            >
              <Plus size={13} className="transition-transform duration-150 group-hover:rotate-90" />
              Add platform
            </button>
          </PopoverTrigger>
          <PopoverContent variant="dark" align="start" className="w-[240px] p-1">
            <div className="space-y-0.5">
              {remaining.map(p => {
                const label = labelForPlatform(p)
                const conn = connections[p]
                const needsAuth = !!conn?.needsConnection && !conn.connected

                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => {
                      onAdd(p)
                      setAddOpen(false)
                    }}
                    className="group relative flex w-full cursor-pointer items-center gap-2.5 rounded-[8px] px-2 py-2 text-left text-paper transition-all duration-150 hover:translate-x-[2px] hover:bg-bg-3"
                  >
                    <PlatformIcon
                      channel={p}
                      size={28}
                      className="transition-transform duration-150 group-hover:scale-105"
                    />
                    <span className="flex-1 truncate text-[13px] font-medium">{label}</span>
                    {needsAuth && (
                      <span
                        className="h-1.5 w-1.5 shrink-0 rounded-full bg-paper-faint"
                        title="Needs connection"
                        aria-label="Needs connection"
                      />
                    )}
                    {/* Trailing + — a faint chip that brightens on row hover,
                        signaling "tap to add". */}
                    <span
                      aria-hidden
                      className="group-hover:text-ink-on-acid flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-bg-4 text-paper-faint transition-colors duration-150 group-hover:bg-acid"
                    >
                      <Plus size={12} strokeWidth={2.5} />
                    </span>
                  </button>
                )
              })}
            </div>
          </PopoverContent>
        </Popover>
      )}

      {/* Customized marker — the active platform variant was hand-edited and
          no longer auto-updates when the post is edited. Reset re-derives it
          from the post (ComposerPage confirms first, then calls the async
          rederive endpoint). */}
      {activePinnedAt && (
        <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">
          <span
            className="flex items-center gap-1.5 rounded-full bg-bg-3 px-2.5 py-1 text-[11px] font-medium text-paper-dim"
            title={`You customized the ${active ? labelForPlatform(active) : ''} version. It won't auto-update when you edit the post.`}
          >
            <PenLine size={11} />
            {activeBehind ? 'Customized · post changed' : 'Customized'}
          </span>
          {onResetToDerived && (
            <button
              type="button"
              onClick={onResetToDerived}
              className="shrink-0 cursor-pointer rounded text-[11px] font-medium text-acid transition-opacity outline-none hover:opacity-80 focus-visible:ring-2 focus-visible:ring-acid"
            >
              Reset to match post
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export function ComposerShell({
  creative,
  projectId,
  platformTabs,
  chatCreative,
  variantView,
}: ComposerShellProps) {
  const isMobile = useIsMobile()
  // One Post model: the agent + quality score belong to the POST, whatever is
  // on screen. `creative` stays the display document (preview, editors).
  const chatDoc = chatCreative ?? creative

  // A published (live) creative is view-only — editing surfaces below read this
  // via ComposerReadOnlyProvider and disable themselves. Mirrors the disabled
  // Schedule button in ComposerHeader (also keyed on publishedAt).
  const readOnly = !!creative.publishedAt
  const [panelOpen, setPanelOpen] = useState(!isMobile)
  const [selectedSection, setSelectedSection] = useState<MobileSelectedSection | null>(null)
  const [sheetMode, setSheetMode] = useState<SheetMode>('chat')
  const [successAction, setSuccessAction] = useState<'copy' | 'download' | null>(null)
  const successTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const generatingFields = useCreativeStore(s => s.generatingFields.get(creative.id)) ?? EMPTY_SET
  const updateCreativeContent = useCreativeStore(s => s.updateCreativeContent)
  const fetchCreatives = useCreativeStore(s => s.fetchCreatives)
  const genStatus = useGenerationStatusStore(s => s.statuses[creative.id]?.status)
  const setContext = useSectionContextStore(s => s.setContext)
  const { track } = useAnalytics()

  // Self-heal the preview when a generation finishes while the composer is open.
  // The live content/welcome events (CreativeContentUpdated, message-created) are
  // best-effort Pusher notifications — a long async reel render can complete after a
  // freshly-loaded tab subscribed, or a beat can be dropped, leaving the preview stuck
  // on the poster. On the authoritative genStatus→'ready' (CreativeGenerationComplete →
  // reconcile) refetch the creative once so the video/caption land without a reload.
  // (The ScorePanel already refetches its snapshot on the same transition.) Reset the
  // guard when a new generation starts so a later run reconciles again.
  const reconciledOnReadyRef = useRef(false)
  useEffect(() => {
    if (genStatus === 'generating') reconciledOnReadyRef.current = false
    if (genStatus === 'ready' && !reconciledOnReadyRef.current) {
      reconciledOnReadyRef.current = true
      void fetchCreatives()
    }
  }, [genStatus, fetchCreatives])

  // Authoritative status convergence. Everything that "fills in" when a build finishes
  // (this shell's content refetch, the ScorePanel snapshot, the CompositionEditorProvider canvas)
  // keys off genStatus→'ready'. That flip rides a single, unbuffered CreativeGenerationComplete
  // Pusher beat — open the composer mid-build and miss/early that beat and the post stays stuck on
  // the pre-build UI until a manual refresh. So: reconcile the DB status on open (covers a composer
  // opened without ever seeing the 'generating' start), then poll the DB while it still looks like
  // it's building so a dropped beat self-corrects. Bounded; stops the moment the status is terminal.
  useEffect(() => reconcileCreativeStatus(creative.id), [creative.id])
  useEffect(() => {
    // Once the status is terminal we're done — the genStatus→'ready' flip fills in the
    // ScorePanel snapshot, this shell's content, the editor canvas, and clears the chat's
    // "Creating your post…".
    if (genStatus === 'ready' || genStatus === 'failed') return
    // Otherwise POLL to converge on the authoritative DB status. The 'ready' flip rides a
    // single, unbuffered CreativeGenerationComplete beat: open the composer mid-build and miss
    // it — OR land on a generation path that left generationStatus null (reconcile's
    // null+content→ready fallback then resolves it) — and the post stays stuck on the building
    // UI with no score forever. Poll whenever the status isn't terminal (undefined or
    // 'generating'); it self-corrects within ~3s of completion and stops on ready/failed. For a
    // settled post the on-open reconcile flips it to 'ready' before the first tick, so this
    // never actually polls a finished post.
    let attempts = 0
    const MAX = 40 // ~2 min at 3s — a safety cap, not the expected lifetime
    let cancelFetch: (() => void) | undefined
    const timer = setInterval(() => {
      attempts += 1
      cancelFetch?.()
      cancelFetch = reconcileCreativeStatus(creative.id)
      if (attempts >= MAX) clearInterval(timer)
    }, 3000)
    return () => {
      clearInterval(timer)
      cancelFetch?.()
    }
  }, [genStatus, creative.id])

  const isTyping = useChatStatusStore(s => s.typingCreatives.has(chatDoc.id))
  const hasUnread = useChatStatusStore(s => s.unreadCreatives.has(chatDoc.id))
  const clearUnread = useChatStatusStore(s => s.clearUnread)

  // Clear unread when the user can see the messages:
  // - Desktop: panelOpen is true from mount, so clears immediately
  // - Mobile: only clears when user opens the chat panel
  // Also handles the Pusher race (markUnread fires while user is already viewing)
  useEffect(() => {
    if (hasUnread && panelOpen) {
      clearUnread(chatDoc.id)
    }
  }, [hasUnread, panelOpen, creative.id, clearUnread])

  const showTypingDots = isTyping || generatingFields.size > 0

  const showSuccess = useCallback((action: 'copy' | 'download') => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current)
    setSuccessAction(action)
    successTimerRef.current = setTimeout(() => setSuccessAction(null), 2000)
  }, [])

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current)
    }
  }, [])

  // ─── Mobile action bar handlers ─────────────────────────────────────────

  const handleActionAskAI = useCallback(() => {
    if (!selectedSection) return
    setContext(chatDoc.id, {
      sectionId: selectedSection.sectionId,
      sectionType: selectedSection.sectionType,
      label: selectedSection.label,
      preview: selectedSection.preview,
    })
    setSheetMode('chat')
    setPanelOpen(true)
    setSelectedSection(null)
  }, [creative.id, selectedSection, setContext])

  const handleActionEdit = useCallback(() => {
    if (!selectedSection) return
    setSheetMode('section-editor')
    setPanelOpen(true)
  }, [selectedSection])

  const handleActionCopy = useCallback(async () => {
    if (!selectedSection) return
    const content = creative.content
    let text = ''

    if (selectedSection.creativeType === 'instagram_post') {
      const igContent = content as InstagramPostContent
      if (selectedSection.sectionType === 'caption') text = igContent.caption
      if (selectedSection.sectionType === 'hashtags')
        text = igContent.hashtags.map(h => `#${h}`).join(' ')
    } else if (selectedSection.creativeType === 'facebook_post') {
      const fbContent = content as FacebookPostContent
      if (selectedSection.sectionType === 'message') text = fbContent.message
    } else if (selectedSection.creativeType === 'tiktok_video') {
      const tiktokContent = content as TikTokVideoContent
      if (selectedSection.sectionType === 'caption') text = tiktokContent.caption
      if (selectedSection.sectionType === 'hashtags')
        text = tiktokContent.hashtags.map(h => `#${h}`).join(' ')
    } else if (selectedSection.creativeType === 'linkedin_post') {
      const linkedinContent = content as LinkedInPostContent
      if (selectedSection.sectionType === 'caption') text = linkedinContent.caption
      if (selectedSection.sectionType === 'hashtags')
        text = linkedinContent.hashtags.map(h => `#${h}`).join(' ')
    } else if (selectedSection.creativeType === 'x_post') {
      const xContent = content as XPostContent
      if (selectedSection.sectionType === 'text') text = xContent.text || ''
      if (selectedSection.sectionType === 'hashtags')
        text = (xContent.hashtags || []).map(h => `#${h}`).join(' ')
    } else if (selectedSection.creativeType === 'youtube_short') {
      const ytContent = content as YouTubeShortsContent
      if (selectedSection.sectionType === 'title') text = ytContent.title
      if (selectedSection.sectionType === 'description') text = ytContent.description
      if (selectedSection.sectionType === 'hashtags')
        text = ytContent.hashtags.map(h => `#${h}`).join(' ')
    } else if (selectedSection.creativeType === 'pinterest_pin') {
      const pinContent = content as PinterestPinContent
      if (selectedSection.sectionType === 'title') text = pinContent.title
      if (selectedSection.sectionType === 'description') text = pinContent.description
    }

    if (text) {
      try {
        await navigator.clipboard.writeText(text)
        showSuccess('copy')
        track(AnalyticsEvents.Composer.COMPOSER_PREVIEW_CONTENT_COPIED, {
          creative_id: creative.id,
          creative_type: creative.type,
          field: selectedSection.sectionType,
        })
      } catch {
        // clipboard blocked
      }
    }
  }, [creative, selectedSection, track, showSuccess])

  const handleActionDownload = useCallback(async () => {
    if (!selectedSection) return
    const content = creative.content as
      InstagramPostContent | FacebookPostContent | LinkedInPostContent | XPostContent
    if (!content.imageUrl && !content.imageAttachmentId) return
    const labelMap: Partial<Record<CreativeType, string>> = {
      [CreativeType.FacebookPost]: 'facebook',
      [CreativeType.LinkedInPost]: 'linkedin',
      [CreativeType.XPost]: 'x',
    }
    const label = labelMap[creative.type] || 'instagram'
    const filename = `${(creative.name || `${label}_post`).replace(/\s+/g, '_')}_${label}.jpg`

    try {
      if (content.imageAttachmentId) {
        const { url } = await openApiRequest<{ url: string }>(OpenAPI, {
          method: 'GET',
          url: '/v1/media/{id}/download-url',
          path: { id: content.imageAttachmentId },
          query: { filename },
        })
        const a = document.createElement('a')
        a.href = url
        a.rel = 'noopener noreferrer'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      } else if (content.imageUrl) {
        const a = document.createElement('a')
        a.href = content.imageUrl
        a.download = filename
        a.rel = 'noopener noreferrer'
        a.target = '_blank'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
      showSuccess('download')
      track(AnalyticsEvents.Composer.COMPOSER_PREVIEW_IMAGE_DOWNLOADED, {
        creative_id: creative.id,
        creative_type: creative.type,
      })
    } catch {
      // silently fail
    }
  }, [creative, selectedSection, track, showSuccess])

  const handleOpenChat = useCallback(() => {
    setSheetMode('chat')
    setPanelOpen(true)
  }, [])

  const handleCloseSheet = useCallback(() => {
    setPanelOpen(false)
    setSheetMode('chat')
    setSelectedSection(null)
  }, [])

  const handleSaveEdit = useCallback(
    async (fields: Record<string, unknown>) => {
      setPanelOpen(false)
      setSheetMode('chat')
      setSelectedSection(null)
      const before = creative.content as unknown as Record<string, unknown>
      const after = { ...before, ...fields }
      if (!creative.projectId || !isUserMediaChange(before, after)) {
        updateCreativeContent(creative.id, fields)
        return
      }
      try {
        await saveUserMediaEdit({
          creativeId: creative.id,
          projectId: creative.projectId,
          before,
          after,
        })
      } catch (err) {
        if (isUserMediaRefusal(err)) console.log('Upload post media change refused:', err)
        else console.error('Upload post media change failed:', err)
        toast.error(extractApiError(err, "We couldn't update the media. Please try again."))
      }
    },
    [creative.id, creative.content, creative.projectId, updateCreativeContent]
  )

  const handleDiscardEdit = useCallback(() => {
    setPanelOpen(false)
    setSheetMode('chat')
    setSelectedSection(null)
  }, [])

  const handlePreviewAreaClick = useCallback(() => {
    if (selectedSection) {
      setSelectedSection(null)
    }
  }, [selectedSection])

  // Mobile full-touch editor: driven by the composition load status (published by the provider)
  // so the swap is immediate — no flash of the old preview while the composition loads.
  const compStatus = useCompositionStatus(s => s.byId[creative.id])
  const compKind = useCompositionStatus(s => s.kindById[creative.id])

  // Preview-first home: tapping a region of the post opens that region's edit BOTTOM SHEET
  // (image / caption / hashtags). Each sheet edits the section manually and offers an "Ask Taka"
  // action that closes the sheet and opens the Ask-Taka chat with that section's context.
  const [editMode, setEditMode] = useState<
    'image' | 'carousel' | 'video' | 'caption' | 'hashtags' | null
  >(null)
  const openDrill = useCallback(
    (section: MobileSelectedSection | null) => {
      if (!section) return
      const t = section.sectionType
      if (t === 'caption' || t === 'hashtags') {
        setSelectedSection(section)
        setEditMode(t)
        return
      }
      if (t === 'image') {
        setSelectedSection(section)
        const ig = creative.content as InstagramPostContent
        const isCarousel = !!(ig.mediaItems && ig.mediaItems.length >= 2)
        const singleImage = !!(ig.imageUrl || ig.imageAttachmentId) && !isCarousel
        if (compKind === 'video' && compStatus === 'canvas') {
          // A reel with an editable video composition → the mobile reel editor (touch timeline +
          // per-beat overlay canvas). Label normalized so the sheet footer reads "reel".
          setSelectedSection({ ...section, label: 'Reel' })
          setEditMode('video')
        } else if (singleImage) {
          // Single-image post → the mobile image editor (touch canvas + layer tools).
          setEditMode('image')
        } else if (isCarousel && compStatus === 'canvas') {
          // A carousel with an editable deck → the mobile deck editor (slide strip + per-slide
          // touch canvas + slide management). Normalize the section label so the sheet's whole-deck
          // "Ask Taka about the …" footer reads "carousel", not the tapped region's "image".
          setSelectedSection({ ...section, label: 'Carousel' })
          setEditMode('carousel')
        } else {
          // No editable canvas (legacy carousel / video) → Ask Taka scoped to the tapped media.
          setContext(chatDoc.id, {
            sectionId: section.sectionId,
            sectionType: section.sectionType,
            label: section.label,
            preview: section.preview,
          })
          setSheetMode('chat')
          setPanelOpen(true)
        }
      }
    },
    [creative.content, creative.id, compStatus, compKind, setContext]
  )
  // Ask Taka about ONE carousel slide: pin a "Slide N of M" context (so the worker's
  // interpretCarouselFeedback targets that slide), close the deck sheet, and open the chat.
  const askTakaForSlide = useCallback(
    (index: number, slideCount: number) => {
      // Count + preview come from the live DECK (slideCount passed by the strip), not
      // creative.content.mediaItems — the rendered masters lag the deck after an in-session
      // add/delete/reorder, so only quote a master's altText when the two are still index-aligned
      // (the same guard InstagramPostPreview uses before quoting slide text into the chat).
      const items = (creative.content as InstagramPostContent).mediaItems ?? []
      const aligned = items.length === slideCount
      const preview = (aligned && items[index]?.altText) || `Slide ${index + 1}`
      setContext(chatDoc.id, {
        sectionId: carouselSlideFocusKey(index),
        sectionType: 'image',
        label: `Slide ${index + 1} of ${slideCount}`,
        preview,
      })
      setEditMode(null)
      setSheetMode('chat')
      setPanelOpen(true)
    },
    [creative.content, creative.id, setContext]
  )
  // Pin a "Beat N" chat context from the video timeline dock + focus the chat, so an AI revise
  // knows which beat the user means (the beat track lives in the bottom dock, inside the provider).
  const askTakaForBeat = useCallback(
    (index: number) => {
      setContext(chatDoc.id, {
        sectionId: `video-beat:${index}`,
        sectionType: 'image',
        label: `Beat ${index + 1}`,
        preview: 'Reel beat',
      })
      setEditMode(null)
      setSheetMode('chat')
      setPanelOpen(true)
    },
    [creative.id, setContext]
  )
  const askTakaForSection = useCallback(() => {
    if (selectedSection) {
      setContext(chatDoc.id, {
        sectionId: selectedSection.sectionId,
        sectionType: selectedSection.sectionType,
        label: selectedSection.label,
        preview: selectedSection.preview,
      })
    }
    setEditMode(null) // release the edit sheet (animates down)…
    setSheetMode('chat') // …and open the Ask-Taka chat (animates up) with the section context.
    setPanelOpen(true)
  }, [creative.id, selectedSection, setContext])

  // Keep each edit sheet's body mounted through its close animation (no blank mid-slide).
  const imageSheetMounted = useExitMount(editMode === 'image')
  const carouselSheetMounted = useExitMount(editMode === 'carousel')
  const videoSheetMounted = useExitMount(editMode === 'video')
  const captionSheetMounted = useExitMount(editMode === 'caption')
  const hashtagsSheetMounted = useExitMount(editMode === 'hashtags')

  if (isMobile) {
    // One Post model: platform tabs on mobile render the same provider-driven
    // pane (sections carry the contract; touch uses the same overlays).
    if (variantView) {
      return (
        <ComposerReadOnlyProvider readOnly={readOnly}>
          <div className="flex flex-1 flex-col overflow-hidden bg-bg">
            {platformTabs && <PlatformTabStrip {...platformTabs} />}
            <VariantModeProvider value={variantView}>
              <div className="relative min-w-0 flex-1 overflow-y-auto bg-bg-2">
                <div className="relative flex min-h-full items-start justify-center px-4 py-6">
                  <div className={DARK_PREVIEW_OVERRIDES}>
                    {getPreview(creative, generatingFields)}
                  </div>
                </div>
              </div>
            </VariantModeProvider>
          </div>
        </ComposerReadOnlyProvider>
      )
    }
    const hasSelection = !!selectedSection
    // Phase 1: the preview-first home for Instagram composition posts; other platforms keep the
    // legacy section-select flow until the home generalizes (P4).
    const isComposablePost = creative.type === CreativeType.InstagramPost && !readOnly
    const useHome = isComposablePost && compStatus === 'canvas'
    // While the composition is still loading, show a neutral placeholder rather than flashing the
    // legacy preview, then swap to the home (or the legacy flow if 'none').
    const showHomeLoading = isComposablePost && compStatus !== 'canvas' && compStatus !== 'none'

    // Shared footer for the edit bottom sheets: closes the sheet and opens Ask-Taka with context.
    const askTakaFooter = (
      <div className="px-3 py-2.5 pb-[calc(env(safe-area-inset-bottom)+10px)]">
        <button
          type="button"
          onClick={askTakaForSection}
          className="text-ink-on-acid flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-acid text-[14px] font-semibold shadow-[var(--shadow-acid-cta)]"
        >
          <Sparkles size={18} />
          Ask Taka about the {(selectedSection?.label ?? 'post').toLowerCase()}
        </button>
      </div>
    )

    return (
      <ComposerReadOnlyProvider readOnly={readOnly}>
        {/* Preview-first home: clean (undo/redo · score · post preview · glassy Ask-Taka). Tapping
         a region drills into its focused editor + AI. No composition → legacy section flow. */}
        <CompositionEditorProvider key={creative.id} creativeId={creative.id} enabled={!readOnly}>
          <div className="relative flex flex-1 flex-col overflow-hidden bg-bg">
            {platformTabs && <PlatformTabStrip {...platformTabs} />}
            {useHome ? (
              <MobileComposerHome
                creativeId={creative.id}
                onOpenChat={handleOpenChat}
                preview={
                  <div className={DARK_PREVIEW_OVERRIDES}>
                    {getPreview(creative, generatingFields, {
                      selectedSectionId: null,
                      onSectionSelect: openDrill,
                      interactive: false,
                    })}
                  </div>
                }
              />
            ) : showHomeLoading ? (
              <div className="relative flex flex-1 items-center justify-center overflow-hidden bg-bg-2">
                <div
                  className="grid-bg pointer-events-none absolute inset-0"
                  data-grid-bg="emphasized"
                  aria-hidden
                />
                <Loader2 size={22} className="animate-spin text-paper-faint" />
              </div>
            ) : (
              <div
                className="relative flex-1 overflow-y-auto bg-bg-2 p-4"
                onClick={handlePreviewAreaClick}
              >
                <div
                  className="grid-bg pointer-events-none absolute inset-0"
                  data-grid-bg="emphasized"
                  aria-hidden
                />
                <div
                  className="relative mb-3 flex items-center justify-between gap-2"
                  onClick={e => e.stopPropagation()}
                >
                  <ScorePanel creativeId={chatDoc.id} className="min-w-0 flex-1" />
                  <ComposerEditorBar />
                </div>
                <div
                  className="relative flex min-h-full flex-col items-center py-4 pb-36"
                  onClick={e => e.stopPropagation()}
                >
                  <div className={DARK_PREVIEW_OVERRIDES}>
                    {getPreview(creative, generatingFields, {
                      selectedSectionId: selectedSection?.sectionId ?? null,
                      onSectionSelect: setSelectedSection,
                    })}
                  </div>
                  {/* Bring-your-own-content: mobile gets the same tray under the preview. */}
                  {!readOnly && (
                    <ByoMediaTray creative={creative} className="mt-3 w-full max-w-[380px]" />
                  )}
                </div>
              </div>
            )}
            <CompositionDock />

            {/* Legacy chat input bar (non-home only — the home has its own glassy Ask-Taka bar). */}
            {!useHome && !panelOpen && !hasSelection && (
              <div className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-bg-2/95 pb-[env(safe-area-inset-bottom)] backdrop-blur">
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={handleOpenChat}
                    className="flex min-h-[44px] flex-1 items-center rounded-[10px] border border-line-bright bg-bg-3 px-3 text-[13px] text-paper-dim"
                  >
                    {readOnly ? 'This post is live' : 'Edit your creative...'}
                    {showTypingDots && <TypingDots className="ml-auto" />}
                    {hasUnread && !showTypingDots && (
                      <span className="liveDot ml-auto" aria-hidden />
                    )}
                  </button>
                  <button
                    onClick={handleOpenChat}
                    aria-label="Open chat"
                    className="text-ink-on-acid flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-acid shadow-[var(--shadow-acid-cta)]"
                  >
                    <Sparkles size={18} />
                  </button>
                </div>
              </div>
            )}

            {/* Legacy action bar (non-home only — the home drills straight into editors). */}
            {!useHome && hasSelection && !panelOpen && (
              <MobileActionBar
                selection={selectedSection!}
                successAction={successAction}
                onAskAI={handleActionAskAI}
                onEdit={handleActionEdit}
                onCopy={handleActionCopy}
                onDownload={handleActionDownload}
                onClose={() => setSelectedSection(null)}
              />
            )}

            {/* Mobile chat bottom sheet */}
            {sheetMode === 'chat' && (
              <MobileBottomSheet open={panelOpen} onClose={handleCloseSheet}>
                <ComposerReadOnlyProvider readOnly={!!chatDoc.publishedAt}>
                  <SideChat
                    creativeId={chatDoc.id}
                    creativeType={chatDoc.type}
                    projectId={projectId}
                    showHeader={false}
                  />
                </ComposerReadOnlyProvider>
              </MobileBottomSheet>
            )}

            {/* Legacy section editor sheet (non-home only). */}
            {!useHome && sheetMode === 'section-editor' && selectedSection && panelOpen && (
              <>
                <div
                  className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
                  onClick={handleDiscardEdit}
                />
                <div className="fixed inset-x-0 bottom-0 z-50 flex flex-col gap-2 px-3 pb-[calc(env(safe-area-inset-bottom)+8px)]">
                  <MobileSectionEditor
                    creative={creative}
                    selection={selectedSection}
                    onSave={handleSaveEdit}
                    onDiscard={handleDiscardEdit}
                  />
                </div>
              </>
            )}

            {/* Home edit BOTTOM SHEETS — image / caption / hashtags. Each edits the section and
              offers "Ask Taka" (closes this sheet, opens the chat with the section context).
              Open/close animate via MobileBottomSheet's translate transition. */}
            {useHome && (
              <>
                <MobileBottomSheet
                  open={editMode === 'image'}
                  onClose={() => setEditMode(null)}
                  title="Edit image"
                  footer={imageSheetMounted ? askTakaFooter : null}
                >
                  {imageSheetMounted && <MobileCanvasEditor />}
                </MobileBottomSheet>

                <MobileBottomSheet
                  open={editMode === 'carousel'}
                  onClose={() => setEditMode(null)}
                  title="Edit carousel"
                  footer={carouselSheetMounted ? askTakaFooter : null}
                >
                  {carouselSheetMounted && <MobileCarouselEditor onAskAISlide={askTakaForSlide} />}
                </MobileBottomSheet>

                <MobileBottomSheet
                  open={editMode === 'video'}
                  onClose={() => setEditMode(null)}
                  title="Edit reel"
                  footer={videoSheetMounted ? askTakaFooter : null}
                >
                  {videoSheetMounted && <MobileVideoEditor onAskAIBeat={askTakaForBeat} />}
                </MobileBottomSheet>

                <MobileBottomSheet
                  open={editMode === 'caption'}
                  onClose={() => setEditMode(null)}
                  title="Caption"
                  height="half"
                  footer={captionSheetMounted ? askTakaFooter : null}
                >
                  {captionSheetMounted && (
                    <div className="px-4 py-4">
                      <EditableInlineText
                        initialValue={(creative.content as InstagramPostContent).caption ?? ''}
                        onCommit={t => updateCreativeContent(creative.id, { caption: t })}
                        ariaLabel="Edit caption"
                        placeholder="Write a caption…"
                        className="block min-h-[140px] w-full text-[15px] leading-relaxed text-paper outline-none"
                      />
                    </div>
                  )}
                </MobileBottomSheet>

                <MobileBottomSheet
                  open={editMode === 'hashtags'}
                  onClose={() => setEditMode(null)}
                  title="Hashtags"
                  height="half"
                  footer={hashtagsSheetMounted ? askTakaFooter : null}
                >
                  {hashtagsSheetMounted && (
                    <div className="px-4 py-4">
                      <EditableHashtags
                        value={(creative.content as InstagramPostContent).hashtags ?? []}
                        onCommit={h => updateCreativeContent(creative.id, { hashtags: h })}
                        className="text-[15px] text-[#1FA1FF]"
                      />
                    </div>
                  )}
                </MobileBottomSheet>
              </>
            )}
          </div>
        </CompositionEditorProvider>
      </ComposerReadOnlyProvider>
    )
  }

  // ─── Desktop ────────────────────────────────────────────────────────────
  return (
    <ComposerReadOnlyProvider readOnly={readOnly}>
      <div className="flex flex-1 flex-col overflow-hidden bg-bg">
        {platformTabs && <PlatformTabStrip {...platformTabs} />}
        {/* The composer center IS the editor: when the active post has a Composition,
          the media becomes a live canvas (no "edit mode"). The dock + top bar render
          only when an editable composition is present (they no-op otherwise). */}
        <CompositionEditorProvider
          key={creative.id}
          creativeId={creative.id}
          enabled={!readOnly && !variantView}
        >
          {/* Composer-level control bar: the global editing chrome for the WHOLE post
              (undo/redo · versions · add · layers). Governs the entire composition, so it sits
              at the top of the composer, not on the image. Renders nothing without a composition;
              on a platform tab the bar STAYS (empty) so the creative doesn't jump. */}
          {!readOnly && <ComposerControlStrip creativeId={creative.id} tools={!variantView} />}
          <div className="flex flex-1 overflow-hidden">
            {/* One Post model: a platform view is a read-only PREVIEW pane with
                the customize gate — the editor work column renders only for the
                post (and for customized platform copies). */}
            {variantView ? (
              // Platform tab: SAME canvas presentation as the Post tab; the
              // sections carry the shared/adapted/customized contract.
              <VariantModeProvider value={variantView}>
                <div className="relative min-w-0 flex-1 overflow-y-auto bg-bg-2">
                  <div className="grid-bg pointer-events-none absolute inset-0" aria-hidden />
                  <div className="relative flex min-h-full items-start justify-center px-6 py-8">
                    <div className={DARK_PREVIEW_OVERRIDES}>
                      {getPreview(creative, generatingFields)}
                    </div>
                  </div>
                </div>
              </VariantModeProvider>
            ) : (
              <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                <div className="flex min-h-0 flex-1 overflow-hidden">
                  <CompositionDock />
                  <div className="relative min-w-0 flex-1 overflow-y-auto bg-bg-2">
                    <div
                      className="grid-bg pointer-events-none absolute inset-0"
                      data-grid-bg="emphasized"
                      aria-hidden
                    />
                    {/* The glossy editing bar now lives ON the visual (top-center of the photo) —
                        see ComposerCanvasMedia. Clicking empty canvas deselects the active layer. */}
                    <CanvasDeselectArea className="relative flex min-h-full flex-col items-center px-6 py-8">
                      <div className={DARK_PREVIEW_OVERRIDES}>
                        {getPreview(creative, generatingFields)}
                      </div>
                      {/* Bring-your-own-content: the user's hands on their own files
                          (swap / reorder / add / remove). Renders only for user-media
                          posts; Taka's hands stay off the pixels either way. */}
                      {!readOnly && (
                        <ByoMediaTray creative={creative} className="mt-3 w-full max-w-[380px]" />
                      )}
                    </CanvasDeselectArea>
                  </div>
                </div>
                {/* A VIDEO reel edits on a real editor layout: the active beat is the canvas above;
                    the timecoded multi-lane timeline (video/captions/audio) + inspector + render
                    toolbar live in this bottom dock. Renders nothing for non-video posts. */}
                <ReelTimelineDock onAskAIBeat={askTakaForBeat} />
                {/* A CAROUSEL edits on the same layout: slide filmstrip (navigate / add /
                    reorder / delete) docked at the bottom of the composer, like the reel
                    timeline. Renders nothing for non-carousel posts. */}
                <SlideFilmstripDock onAskAISlide={askTakaForSlide} />
              </div>
            )}

            {/* Right rail — the Taka agent, all the way down. Quality panel above the AI chat. */}
            <div className="flex w-[440px] flex-shrink-0 flex-col gap-2 bg-bg p-2 pl-0">
              <ScorePanel creativeId={chatDoc.id} className="flex-shrink-0" />
              <div className="card flex min-h-0 flex-1 flex-col overflow-hidden">
                {/* Chat read-only tracks the POST (its target), not the display
                    variant — a published variant must not mute the post's chat,
                    and a published post must not chat-edit from a variant tab. */}
                <ComposerReadOnlyProvider readOnly={!!chatDoc.publishedAt}>
                  <SideChat
                    creativeId={chatDoc.id}
                    creativeType={chatDoc.type}
                    projectId={projectId}
                    showHeader
                  />
                </ComposerReadOnlyProvider>
              </div>
            </div>
          </div>
        </CompositionEditorProvider>
        <EditorModal creative={creative} />
      </div>
    </ComposerReadOnlyProvider>
  )
}

/** The composer-level control strip (desktop): a full-width T3 bar at the top of the composer
 *  holding the global editing chrome. Renders only when the post has an editable composition
 *  (nothing to control otherwise).
 *
 *  `tools` off (a platform tab, which is a read-only preview) empties the bar but KEEPS it: the
 *  strip is chrome that belongs to the post, and dropping it out of the layout shifted the whole
 *  creative up 45px on every Post ⇄ platform switch. The empty strip holds its height with an
 *  h-8 spacer, the same box the icon buttons occupy, so both states measure identically (a
 *  min-height would have to restate the padding and border, and was 1px short). */
function ComposerControlStrip({ creativeId, tools }: { creativeId: string; tools: boolean }) {
  const hasEditor = useComposerEditor() != null
  const hasComposition = usePostHasComposition(creativeId)
  if (!hasEditor && !hasComposition) return null
  return (
    <div className="flex flex-shrink-0 items-center border-b border-line bg-bg-2 px-3 py-1.5">
      {tools && hasEditor ? <ComposerEditorBar /> : <div className="h-8" aria-hidden />}
    </div>
  )
}
